import { Heart, Plus, Check, ImageOff } from "lucide-react";
import { resolveImageUrl } from "../lib/api";

const rarityColors = {
  "Common": "bg-zinc-700/50 text-zinc-300 border-zinc-600",
  "Uncommon": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  "Rare": "bg-blue-500/10 text-blue-400 border-blue-500/20",
  "Ultra Rare": "bg-amber-500/10 text-amber-400 border-amber-500/20",
};

function ReleaseBadges({ release }) {
  return (
    <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
      {release.is_just_released && (
        <span
          data-testid={`release-just-released-${release.release_id}`}
          className="bg-emerald-400 text-emerald-950 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-[0_0_12px_rgba(52,211,153,0.5)] animate-pulse"
        >
          Just Released
        </span>
      )}
      {release.needs_backfill && (
        <span
          data-testid={`release-stub-${release.release_id}`}
          className="bg-fuchsia-500/90 text-white px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
          title="Needs casting/color backfill"
        >
          Stub
        </span>
      )}
      {release.is_chase && (
        <span className="bg-amber-500/90 text-zinc-950 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">CHASE</span>
      )}
      {release.is_blkltd && (
        <span className="bg-purple-500/90 text-white px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">BLKLTD</span>
      )}
      {release.variant_type === "Exclusive" && (
        <span className="bg-sky-500/90 text-white px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">EXCL</span>
      )}
    </div>
  );
}

function StatusBadges({ isOwned, isWishlisted }) {
  return (
    <div className="absolute top-2 right-2 flex gap-1.5">
      {isOwned && (
        <span className="bg-emerald-500/90 text-white p-1 rounded-full"><Check className="w-3 h-3" /></span>
      )}
      {isWishlisted && (
        <span className="bg-rose-500/90 text-white p-1 rounded-full"><Heart className="w-3 h-3 fill-current" /></span>
      )}
    </div>
  );
}

function CardActions({ release, isOwned, isWishlisted, onAddCollection, onRemoveCollection, onAddWishlist }) {
  return (
    <div className="absolute bottom-2 left-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      {!isOwned && (
        <button
          data-testid={`add-collection-${release.release_id}`}
          onClick={(e) => { e.stopPropagation(); onAddCollection?.(release); }}
          className="flex-1 bg-rose-600 hover:bg-rose-500 text-white text-xs font-medium py-1.5 px-2 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          <Plus className="w-3 h-3" /> Collect
        </button>
      )}
      {isOwned && (
        <button
          data-testid={`remove-collection-${release.release_id}`}
          onClick={(e) => { e.stopPropagation(); onRemoveCollection?.(release); }}
          className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-medium py-1.5 px-2 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          <Check className="w-3 h-3" /> Owned
        </button>
      )}
      {!isWishlisted && !isOwned && (
        <button
          data-testid={`add-wishlist-${release.release_id}`}
          onClick={(e) => { e.stopPropagation(); onAddWishlist?.(release); }}
          className="bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-medium py-1.5 px-2 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          <Heart className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export default function ReleaseCard({ release, onAddCollection, onAddWishlist, onRemoveCollection, onRemoveWishlist, onCardClick, isMissing }) {
  const isOwned = release.owned;
  const isWishlisted = release.wishlisted;
  const imgSrc = resolveImageUrl(release.model_image);
  const value = release.estimated_market_value;

  return (
    <div
      data-testid={`release-card-${release.release_id}`}
      onClick={() => onCardClick?.(release.release_id)}
      className={`group relative bg-zinc-900 border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer ${isMissing ? 'opacity-70 grayscale hover:opacity-100 hover:grayscale-0' : ''} ${
        release.personalized_for
          ? "border-amber-400/40 ring-1 ring-amber-400/30 hover:border-amber-400/60"
          : "border-zinc-800/50 hover:border-zinc-700"
      }`}
    >
      {release.personalized_for && (
        <span
          data-testid={`release-personalized-badge-${release.release_id}`}
          className="absolute top-2 left-2 z-10 bg-amber-400 text-black px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg"
        >
          For You
        </span>
      )}
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-800">
        {imgSrc ? (
          <img src={imgSrc} alt={release.release_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <ImageOff className="w-8 h-8 text-zinc-600 mb-1" />
            <span className="text-[10px] text-zinc-600 font-mono">{release.khmg_number}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent opacity-60" />
        <ReleaseBadges release={release} />
        <StatusBadges isOwned={isOwned} isWishlisted={isWishlisted} />
        <CardActions release={release} isOwned={isOwned} isWishlisted={isWishlisted} onAddCollection={onAddCollection} onRemoveCollection={onRemoveCollection} onAddWishlist={onAddWishlist} />
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1.5 mb-1">
          <span className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${rarityColors[release.rarity] || rarityColors["Common"]}`}>
            {release.rarity}
          </span>
        </div>
        <p className="text-[10px] text-zinc-500 font-mono tracking-wider truncate">{release.khmg_number}</p>
        <h3 className="font-heading text-sm font-medium text-zinc-100 mt-0.5 line-clamp-2 leading-tight">{release.release_name}</h3>
        <div className="flex items-center justify-between mt-2">
          {value != null ? (
            <span className="font-mono text-sm font-semibold text-rose-500">${value.toFixed(2)}</span>
          ) : (
            <span className="font-mono text-xs text-zinc-600">TBD</span>
          )}
          <span className="text-[11px] text-zinc-500 truncate ml-1">{release.vehicle}</span>
        </div>
      </div>
    </div>
  );
}
